export default function ServicesSection() {
  const services = [
    {
      title: "Personal Care Assistance",
      description:
        "Compassionate help with daily activities including bathing, dressing, grooming, and mobility support to maintain dignity and independence.",
      image: "/images/services/personal-care.jpg",
      icon: "ri-heart-pulse-line",
    },
    {
      title: "Skilled Nursing & Specialized Care",
      description:
        "Professional medical care services including wound care, medication management, IV therapy, and specialized treatment plans delivered by licensed nursing professionals.",
      image: "/images/services/skilled-nursing.jpg",
      icon: "ri-stethoscope-line",
    },
    {
      title: "Companion Services",
      description:
        "Friendly companionship, conversation, recreational activities, and emotional support to reduce isolation and enhance quality of life.",
      image: "/images/services/companion.jpg",
      icon: "ri-user-heart-line",
    },
    {
      title: "Light Housekeeping",
      description:
        "Assistance with maintaining a clean and organized living environment including dusting, vacuuming, laundry, and general household organization.",
      image: "/images/services/house-keeping.jpg",
      icon: "ri-home-heart-line",
    },
    {
      title: "Meal Preparation & Nutrition",
      description:
        "Nutritious meal planning and preparation services tailored to dietary needs and preferences, ensuring proper nutrition and enjoyable dining experiences.",
      image: "/images/services/meal-preparation.jpg",
      icon: "ri-restaurant-line",
    },
    {
      title: "Care Management",
      description:
        "Comprehensive coordination of healthcare services, appointment scheduling, and communication with medical providers to ensure seamless care delivery.",
      image: "/images/services/care-management.jpg",
      icon: "ri-calendar-check-line",
    },
    {
      title: "Physical Therapy",
      description:
        "Professional rehabilitation services to improve mobility, strength, balance, and overall physical function for enhanced independence and quality of life.",
      image: "/images/services/physical-therapy.jpg",
      icon: "ri-run-line",
    },
    {
      title: "Speech Therapy",
      description:
        "Specialized communication and swallowing therapy to help individuals regain or improve speech, language, and cognitive communication skills.",
      image: "/images/services/speech-therapy.jpg",
      icon: "ri-speak-line",
    },
    {
      title: "Occupational Therapy",
      description:
        "Adaptive therapy services to help individuals perform daily activities safely and independently through skill development and environmental modifications.",
      image: "/images/services/occupational-therapy.jpg",
      icon: "ri-hand-heart-line",
    },
    {
      title: "CNA/HHA Services",
      description:
        "Certified Nursing Assistant and Home Health Aide services providing professional personal care, vital sign monitoring, and daily living assistance under medical supervision.",
      image: "/images/services/cna-hha.jpg",
      icon: "ri-nurse-line",
    },
  ];

  return (
    <section
      id="services"
      className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-gray-50 to-blue-50"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16 md:mb-20">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            Our Care Services
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl sm:max-w-4xl mx-auto leading-relaxed px-2">
            Comprehensive support services designed to help seniors and
            individuals maintain their independence while receiving the care
            they deserve
          </p>
        </div>

        {/* Hero Service - Full Width */}
        <div className="mb-12 sm:mb-16">
          <div className="relative group bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300 cursor-pointer h-64 sm:h-80 md:h-96">
            <img
              src={services[0].image}
              alt={services[0].title}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>

            <div className="relative h-full flex items-center">
              <div className="max-w-2xl px-4 sm:px-6 md:px-8 lg:px-12">
                <div className="flex flex-col sm:flex-row items-start sm:items-center mb-3 sm:mb-4 gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-teal-600 rounded-full flex-shrink-0">
                    <i
                      className={`${services[0].icon} text-white text-lg sm:text-xl`}
                    ></i>
                  </div>
                  <span className="inline-block px-3 sm:px-4 py-1 sm:py-2 bg-teal-600 text-white text-xs sm:text-sm font-semibold rounded-full">
                    Featured Service
                  </span>
                </div>
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
                  {services[0].title}
                </h3>
                <p className="text-sm sm:text-base md:text-lg text-gray-200 leading-relaxed">
                  {services[0].description}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Services - Connected Layout */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden mb-8 sm:mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {services.slice(1, 3).map((service, index) => (
              <div
                key={index + 1}
                className={`relative group h-64 sm:h-72 md:h-80 overflow-hidden ${
                  index === 0 ? "lg:border-r border-gray-200" : ""
                }`}
              >
                <img
                  src={service.image}
                  alt={service.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent group-hover:from-black/90 transition-all duration-300"></div>

                <div className="absolute inset-0 p-4 sm:p-6 md:p-8 flex flex-col justify-end">
                  <div className="flex items-center mb-2 sm:mb-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-white/20 rounded-full mr-2 sm:mr-3">
                      <i
                        className={`${service.icon} text-white text-base sm:text-lg`}
                      ></i>
                    </div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-gray-200 text-xs sm:text-sm leading-relaxed opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Services - Flowing Grid */}
        <div className="space-y-6 sm:space-y-8">
          {/* First Row - 3 Services */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
              {services.slice(3, 6).map((service, index) => (
                <div
                  key={index + 3}
                  className={`relative group h-56 sm:h-64 overflow-hidden ${
                    index < 2 ? "md:border-r border-gray-200" : ""
                  }`}
                >
                  <img
                    src={service.image}
                    alt={service.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />

                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/70 transition-all duration-300 flex items-end">
                    <div className="p-4 sm:p-6 w-full">
                      <div className="flex items-center mb-2">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-white/20 rounded-full mr-2">
                          <i
                            className={`${service.icon} text-white text-xs sm:text-sm`}
                          ></i>
                        </div>
                        <h3 className="text-base sm:text-lg font-bold text-white">
                          {service.title}
                        </h3>
                      </div>
                      <p className="text-gray-200 text-xs leading-relaxed opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Second Row - 4 Services */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
              {services.slice(6, 10).map((service, index) => (
                <div
                  key={index + 6}
                  className={`relative group h-56 sm:h-64 overflow-hidden ${
                    index < 3 ? "md:border-r border-gray-200" : ""
                  }`}
                >
                  <img
                    src={service.image}
                    alt={service.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />

                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/70 transition-all duration-300 flex items-end">
                    <div className="p-4 sm:p-6 w-full">
                      <div className="flex items-center mb-2">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-white/20 rounded-full mr-2">
                          <i
                            className={`${service.icon} text-white text-xs sm:text-sm`}
                          ></i>
                        </div>
                        <h3 className="text-base sm:text-lg font-bold text-white">
                          {service.title}
                        </h3>
                      </div>
                      <p className="text-gray-200 text-xs leading-relaxed opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12 sm:mt-16">
          <div className="inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-4 bg-white rounded-2xl sm:rounded-full px-6 sm:px-8 py-4 shadow-lg">
            <span className="text-gray-700 font-medium text-sm sm:text-base">
              Ready to get started?
            </span>
            <button
              onClick={() => {
                const element = document.getElementById("contact");
                if (element) {
                  element.scrollIntoView({ behavior: "smooth" });
                }
              }}
              className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 rounded-full font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm sm:text-base"
            >
              Contact Us Today
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
